/**
 * CR283: Merchant Scan Card Component
 */

import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import type { MerchantDirectoryRow, MerchantSource } from "@/hooks/useMerchantsDirectory";
import { useMerchantDirectoryMutations } from "@/hooks/useMerchantsDirectory";

interface MerchantScanCardProps {
  merchant: MerchantDirectoryRow;
  sources: MerchantSource[];
  loading: boolean;
}

export function MerchantScanCard({ merchant, sources, loading }: MerchantScanCardProps) {
  const { setScanEnabled, isSettingScan } = useMerchantDirectoryMutations();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scanning</CardTitle>
        <CardDescription>
          Control scanning for all sources. Toggle affects all sources at once.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Master Scan Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-0.5">
            <Label className="text-base">Scan Enabled</Label>
            <div className="text-sm text-muted-foreground">
              {merchant.active_sources_count} of {merchant.sources_count} sources active
            </div>
          </div>
          {merchant.sources_count > 0 ? (
            <Switch
              checked={merchant.scan_enabled}
              onCheckedChange={(enabled) =>
                setScanEnabled({ merchantKey: merchant.merchant_key, enabled })
              }
              disabled={isSettingScan}
            />
          ) : (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/merchant-sources?merchant=${merchant.merchant_key}`}>
                <Plus className="h-4 w-4 mr-1" />
                Add Source
              </Link>
            </Button>
          )}
        </div>

        {/* Sources List */}
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : sources.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No sources configured for this merchant.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => (
                <TableRow key={source.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{source.source_label}</span>
                      <a
                        href={source.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={source.is_active ? "default" : "secondary"}>
                      {source.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{source.priority}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(source.updated_at), "MMM d")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {sources.length > 0 && (
          <Button variant="outline" size="sm" asChild className="w-full">
            <Link to={`/admin/merchant-sources?merchant=${merchant.merchant_key}`}>
              Manage Sources
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
